import React, { useEffect, useState } from 'react';
import { Modal, Spin } from 'antd';
import { useRoomLifeCycleStateQuery } from '../../../../../../../queries/roomLifeCycleState';
import { useFormController } from '../../../../../../../helpers/formManager';
import { getChangedLifeCycleData } from '../../utils/getChangedLifeCycleData';
import { createLifeCycleUpdater } from '../../utils/createLifeCycleUpdater';
import FetchLifeCycleOptionsErrorAlert from './FetchLifeCycleOptionsErrorAlert';
import {
  lifeCycleEntitiesMap,
  addressIdFieldName,
  selectFieldNames,
} from '../../constants/lifeCycle';
import Body from './Body';
import { useModalVisible, useSetModalVisible } from '../../services/modalState';
import { useSyncUpdateFlatsGrid } from '../../../../../queries/useFlatsGrid';
import { roomStageIdsMap } from '../../../../../../../constants/room/lifeCycle';
import styles from './index.module.scss';

const labels = {
  [lifeCycleEntitiesMap.stage]: 'стадии',
  [lifeCycleEntitiesMap.step]: 'этапа',
  [lifeCycleEntitiesMap.acceptanceStatus]: 'результата приемки',
};

const LifeCycleModal = ({
  getInitialValue,
  getOnSubmit,
  entity,
  FormComponent,
  confirmLoading,
  room,
  flatsGridKey,
}) => {
  const [changedLifeCycleData, setChangedLifeCycleData] = useState(null);

  const [isFetchOptionsError, setIsFetchOptionsError] = useState(false);

  const visible = useModalVisible();
  const setVisible = useSetModalVisible();

  const syncUpdateFlatsGrid = useSyncUpdateFlatsGrid();

  const {
    data: lifeCycleState,
    isFetching,
    refetch,
    remove,
  } = useRoomLifeCycleStateQuery({
    params: { addressId: room?.addressId },
    options: { enabled: Boolean(visible && room?.addressId) },
  });

  const canChange = !isFetching && checkCanChange(lifeCycleState, room);

  useEffect(() => {
    if (visible && typeof lifeCycleState === 'object' && room != null) {
      const changedData = getChangedLifeCycleData(room, lifeCycleState);

      if (changedData) {
        const updater = createLifeCycleUpdater(room.addressId, lifeCycleState);
        syncUpdateFlatsGrid(flatsGridKey, updater);

        if (canChange) {
          setChangedLifeCycleData(changedData);
        }
      }
    }
  }, [
    room,
    flatsGridKey,
    lifeCycleState,
    syncUpdateFlatsGrid,
    canChange,
    visible,
  ]);

  const initialValue = getInitialValue(room, lifeCycleState);

  const values = {
    [selectFieldNames[entity]]: initialValue,
    [addressIdFieldName]: room?.addressId,
  };

  const { handleSubmit, control, reset } = useFormController({
    mode: 'onChange',
    values,
  });

  const closeModal = () => {
    setVisible(false);
  };

  const afterClose = () => {
    if (changedLifeCycleData) {
      setChangedLifeCycleData(null);
    }

    if (isFetchOptionsError) {
      setIsFetchOptionsError(false);
    }

    reset();
    remove();
  };

  const handleOk = () => {
    switch (true) {
      case Boolean(changedLifeCycleData): {
        setChangedLifeCycleData(null);
        break;
      }

      case canChange: {
        const onSubmit = getOnSubmit({ closeModal });
        const submit = handleSubmit(onSubmit);
        submit();
        break;
      }

      default: {
        closeModal();
      }
    }
  };

  return (
    <Modal
      title={`Изменение ${labels[entity]} - ${room?.address?.typeName} ${room?.address?.name}`}
      visible={visible}
      destroyOnClose
      onOk={handleOk}
      onCancel={closeModal}
      okButtonProps={{
        disabled: isFetching || isFetchOptionsError || !canChange,
      }}
      centered
      confirmLoading={confirmLoading}
      afterClose={afterClose}
    >
      <Spin spinning={isFetching}>
        <div className={styles.body}>
          {lifeCycleState === 'Error' && (
            <FetchLifeCycleOptionsErrorAlert
              refetch={refetch}
              entity={entity}
              canChange={canChange}
            />
          )}

          {!isFetching && (
            <Body
              canChange={canChange}
              changedLifeCycleData={changedLifeCycleData}
              entity={entity}
              roomId={room?.addressId}
              control={control}
              initialValue={initialValue}
              setIsFetchOptionsError={setIsFetchOptionsError}
              FormComponent={FormComponent}
            />
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default LifeCycleModal;

function checkCanChange(lifeCycleState, room) {
  const stageId =
    lifeCycleState?.stage?.id ??
    room?.addressLifeCycle?.stageAddressLifeCycle?.id;
  return stageId !== roomStageIdsMap.acceptance;
}
